/**
 * COMPENDIUM data adapter — the in-fiction OMNINET compendium archive, backed by the real
 * {@link Compendium}: sections, faceting, manufacturer accents and cross-linked detail panes,
 * every record read off the loaded packs.
 *
 * Two of the browsable "types" aren't Compendium item types and are reconstructed here:
 *  - **manufacturer** — brand records from `listManufacturers()` (colors + quote + lore).
 *  - **license** — *synthesized*: core `@massif/lancer-data` ships no `licenses.json`, so
 *    we group frames/weapons/systems/mods by their `license` name into an unlock ladder
 *    keyed on `license_level`. A frame's license is its own name (GMS frames → "GMS").
 *
 * Everything in this module is pure data shaping over a `Compendium` snapshot — no React,
 * no side effects — so the view-model builders ({@link computeBrowse}, {@link computeSearch},
 * {@link buildDetail}) are unit-testable in isolation.
 */
import {
  type Compendium,
  type ItemType,
  type Frame,
  type Weapon,
  type System,
  type Mod,
  type Talent,
  type Skill,
  type Tag,
  type PilotGear,
  type CoreBonus,
  type TagRef,
  type Damage,
  type Range as RangeVal,
  type NpcClass,
  type NpcTemplate,
  type NpcFeature,
  listManufacturers,
  type Manufacturer,
  type Background,
} from "@lancer-vtt/compendium";
import { licenseSlug } from "../../lib/editor/compendiumQuery";

// ---- type system over the extended catalog ------------------------------------------

/** Every browsable category — Compendium item types plus the three reconstructed ones. */
export type CompendiumType = ItemType | "manufacturer" | "background";

export type SectionId = "MECHS" | "PILOTS" | "NPCS" | "GLOSSARY";

/** A pointer to one record, the unit of selection + cross-linking. */
export type Ref = { type: CompendiumType; id: string };

/** The COMPENDIUM window's full UI state (everything the component drives off). */
export type CompendiumState = {
  open: boolean;
  section: SectionId;
  category: CompendiumType;
  filter: string;
  /** Active facet selections, keyed by axis (`maker`/`mount`/`sp`/`ll`/`fit`/`role`). AND-combined. */
  facets: Record<string, string>;
  groupBy: boolean;
  selected: Ref | null;
  searchText: string;
};

export const INITIAL_STATE: CompendiumState = {
  open: true,
  section: "MECHS",
  category: "frame",
  filter: "",
  facets: {},
  groupBy: false,
  selected: { type: "frame", id: "mf_standard_pattern_i_everest" },
  searchText: "",
};

// ---- static config ------------------------------------------------------------------

export const SECTIONS: Record<SectionId, [CompendiumType, string][]> = {
  MECHS: [
    ["frame", "Frames"],
    ["weapon", "Weapons"],
    ["system", "Systems"],
    ["mod", "Mods"],
  ],
  PILOTS: [
    ["talent", "Talents"],
    ["skill", "Skills"],
    ["pilot_gear", "Pilot Gear"],
    ["core_bonus", "Core Bonuses"],
    ["license", "Licenses"],
    ["background", "Backgrounds"],
    ["manufacturer", "Manufacturers"],
  ],
  NPCS: [
    ["npc_class", "Classes"],
    ["npc_template", "Templates"],
    ["npc_feature", "Features"],
  ],
  GLOSSARY: [["tag", "Tags"]],
};

const TYPE_LABEL: Record<CompendiumType, string> = {
  frame: "Frames",
  weapon: "Weapons",
  system: "Systems",
  mod: "Mods",
  talent: "Talents",
  skill: "Skills",
  pilot_gear: "Pilot Gear",
  core_bonus: "Core Bonuses",
  license: "Licenses",
  background: "Backgrounds",
  manufacturer: "Manufacturers",
  npc_class: "Classes",
  npc_template: "Templates",
  npc_feature: "Features",
  rollable_table: "Tables",
  tag: "Tags",
};

const TYPE_SING: Record<CompendiumType, string> = {
  frame: "MECH CHASSIS",
  weapon: "MECH WEAPON",
  system: "MECH SYSTEM",
  mod: "WEAPON MOD",
  talent: "PILOT TALENT",
  skill: "SKILL TRIGGER",
  pilot_gear: "PILOT GEAR",
  core_bonus: "CORE BONUS",
  license: "LICENSE",
  background: "PILOT ORIGIN",
  manufacturer: "MANUFACTURER",
  npc_class: "NPC CLASS",
  npc_template: "NPC TEMPLATE",
  npc_feature: "NPC FEATURE",
  rollable_table: "ROLL TABLE",
  tag: "RULES KEYWORD",
};

const ROMAN = ["", "I", "II", "III", "IV", "V"];
/**
 * The neutral fallback accent for a record with no real manufacturer brand color — a
 * desaturated ink-grey. A record only reaches this when its source isn't a known manufacturer
 * (a community pack with no brand record, or a non-maker source); a recognized maker always
 * gets its own `manufacturer.dark` (see {@link mfrStyle}).
 */
const NEUTRAL_ACCENT = "#5a5a5a";
const NEUTRAL_TINT = "#ecebe7";

const DMG_COL: Record<string, string> = {
  Kinetic: "#111",
  Energy: "#2f5f8c",
  Explosive: "#c0240f",
  Burn: "#a3551c",
  Heat: "#a3551c",
  Variable: "#555",
};

/**
 * Real brand colors, keyed by manufacturer id, built once from `listManufacturers()`. Every
 * maker — community-pack makers included — recolors to its own brand `manufacturer.dark` (the
 * dark-field accent); a maker with no brand record falls back to {@link NEUTRAL_ACCENT}. The
 * `tint` is the faint archive-ground wash derived from the brand color for grouped-list headers.
 */
const BRAND_BY_ID = new Map(listManufacturers().map((m) => [m.id, m.dark]));

/** A faint, near-cream wash of a brand color — the grouped-list maker-header ground. */
const tintOf = (accent: string) => `color-mix(in srgb, ${accent} 12%, #f4f2ec)`;

/** Maker display order for the "group by maker" view; unknowns are appended. */
const MFR_ORDER = ["GMS", "IPS-N", "SSC", "HA", "HORUS"];

// ---- synthesized license index ------------------------------------------------------

export type SynthLicense = {
  id: string;
  name: string;
  source?: string;
  /** Unlock ladder: items granted at each license level, ascending. */
  unlocks: { level: number; refs: Ref[] }[];
};

/**
 * Build the license catalog from the gear that references it. Core data has no license
 * records, so we reconstruct each license from its members: a frame belongs to the license
 * named after it (GMS frames → the shared "GMS" license), and weapons/systems/mods declare
 * their `license` name directly. Members are bucketed by `license_level` into the ladder.
 */
function synthLicenses(compendium: Compendium): SynthLicense[] {
  const byName = new Map<string, { name: string; source?: string; levels: Map<number, Ref[]> }>();
  const ensure = (name: string, source?: string) => {
    let lic = byName.get(name);
    if (!lic) {
      lic = { name, source, levels: new Map() };
      byName.set(name, lic);
    }
    if (!lic.source && source) lic.source = source;
    return lic;
  };
  const add = (name: string, source: string | undefined, member: { ref: Ref; level: number }) => {
    const lic = ensure(name, source);
    const bucket = lic.levels.get(member.level) ?? [];
    bucket.push(member.ref);
    lic.levels.set(member.level, bucket);
  };

  for (const f of compendium.list("frame")) {
    const name = f.source === "GMS" ? "GMS" : f.name;
    add(name, f.source, { ref: { type: "frame", id: f.id }, level: f.license_level ?? 0 });
  }
  for (const type of ["weapon", "system", "mod"] as const) {
    for (const it of compendium.list(type)) {
      if (!it.license) continue;
      add(it.license, it.source, { ref: { type, id: it.id }, level: it.license_level ?? 0 });
    }
  }

  return [...byName.values()]
    .map((lic) => ({
      id: licenseSlug(lic.name),
      name: lic.name,
      source: lic.source,
      unlocks: [...lic.levels.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([level, refs]) => ({ level, refs })),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

// ---- the source: unified access over the extended catalog ----------------------------

/** A normalized list/selection row — the minimum the list + subtitle + facet rendering needs. */
export type CompendiumEntry = {
  id: string;
  name: string;
  source?: string;
  license_level?: number;
  gearType?: string;
  /** Weapon mount size (Auxiliary/Main/Heavy/…) — drives the weapon mount facet axis. */
  mount?: string;
  /** SP cost (weapons/systems/mods) — drives the weapon SP facet axis. */
  sp?: number;
  /** Mod weapon-type fit (`allowed_types`) — drives the mod weapon-fit facet axis. */
  allowedTypes?: string[];
  /** NPC class role (Striker/Controller/…) — drives the NPC-class role facet axis. */
  role?: string;
  pack: string;
};

function packLabel(packId: string | undefined): string {
  if (!packId) return "CORE";
  const name = packId.split("@")[0] ?? packId;
  const lower = name.toLowerCase();
  if (lower.includes("long rim")) return "LONG RIM";
  if (lower.includes("core")) return "CORE";
  return name.toUpperCase();
}

/**
 * Wraps a {@link Compendium} with the two reconstructed catalogs (manufacturer,
 * license) and exposes a uniform `list`/`getById`/`count` over every
 * {@link CompendiumType}, plus the accent/subtitle/manufacturer helpers the views need.
 */
export class CompendiumSource {
  readonly manufacturers: Manufacturer[];
  readonly licenses: SynthLicense[];
  private readonly mfrById: Map<string, Manufacturer>;
  private readonly licById: Map<string, SynthLicense>;

  constructor(readonly compendium: Compendium) {
    this.manufacturers = listManufacturers();
    this.licenses = synthLicenses(compendium);
    this.mfrById = new Map(this.manufacturers.map((m) => [m.id, m]));
    this.licById = new Map(this.licenses.map((l) => [l.id, l]));
  }

  manufacturer(id: string | undefined): Manufacturer | undefined {
    return id ? this.mfrById.get(id) : undefined;
  }

  /** The raw record behind a ref (typed loosely; callers narrow by `type`). */
  raw(type: CompendiumType, id: string): Record<string, unknown> | undefined {
    if (type === "manufacturer") return this.mfrById.get(id) as Record<string, unknown> | undefined;
    if (type === "background") return this.compendium.getById("background", id) as Record<string, unknown> | undefined;
    if (type === "license") return this.licById.get(id) as unknown as Record<string, unknown> | undefined;
    return compendiumGet(this.compendium, type, id) as Record<string, unknown> | undefined;
  }

  count(type: CompendiumType): number {
    if (type === "manufacturer") return this.manufacturers.length;
    if (type === "background") return this.compendium.count("background");
    if (type === "license") return this.licenses.length;
    return this.compendium.count(type);
  }

  /** Normalized rows for one category, in source order. */
  list(type: CompendiumType): CompendiumEntry[] {
    if (type === "manufacturer") {
      return this.manufacturers.map((m) => ({ id: m.id, name: m.name, source: m.id, pack: "CORE" }));
    }
    if (type === "license") {
      return this.licenses.map((l) => ({ id: l.id, name: l.name, source: l.source, pack: "CORE" }));
    }
    return this.compendium.list(type).map((it) => this.entry(type, it as Record<string, unknown>));
  }

  private entry(type: CompendiumType, it: Record<string, unknown>): CompendiumEntry {
    const id = it.id as string;
    const allowed = it.allowed_types;
    return {
      id,
      name: (it.name as string) ?? id,
      source: it.source as string | undefined,
      license_level: it.license_level as number | undefined,
      gearType: it.type as string | undefined,
      mount: it.mount as string | undefined,
      sp: typeof it.sp === "number" ? it.sp : undefined,
      allowedTypes: Array.isArray(allowed) ? (allowed as string[]) : undefined,
      role: it.role as string | undefined,
      pack: packLabel(this.compendium.getOwned(type as ItemType, id)?.packId),
    };
  }

  /** Resolve the display name of any ref (for cross-links). */
  nameOf(ref: Ref): string {
    const r = this.raw(ref.type, ref.id);
    return ((r?.name as string) ?? ref.id) || ref.id;
  }

  /**
   * The accent color for a record — its manufacturer's real brand `dark` color when the
   * record is maker-sourced gear (frame/weapon/system/mod/license) or a manufacturer
   * itself, else the neutral fallback. Drives the detail header tint (through `<ThemeScope>`),
   * the grouped-list bars, and the search dots. Brand-driven: a community-pack maker gets its
   * own color, not a generic accent.
   */
  accentOf(type: CompendiumType, source: string | undefined): string {
    if (type === "manufacturer") return mfrStyle(source).accent;
    if (
      type === "frame" || type === "weapon" || type === "system" || type === "mod" ||
      type === "license" || type === "core_bonus"
    ) {
      return mfrStyle(source).accent;
    }
    return NEUTRAL_ACCENT;
  }

  /** The list subtitle for a row — maker + LL for gear, role/etc otherwise. */
  subFor(type: CompendiumType, e: CompendiumEntry): string {
    if (type === "frame" || type === "weapon" || type === "system" || type === "mod") {
      const mn = this.manufacturer(e.source)?.name ?? e.source ?? "";
      const ll = e.license_level != null ? `  LL ${e.license_level}` : "";
      return `${mn}${ll}`;
    }
    if (type === "skill") return "skill trigger";
    if (type === "npc_class") {
      const role = (this.raw("npc_class", e.id)?.role as string | undefined) ?? "";
      return role ? role.toUpperCase() : "npc class";
    }
    if (type === "npc_template") return "npc template";
    if (type === "npc_feature") {
      const ft = (this.raw("npc_feature", e.id)?.type as string | undefined) ?? "";
      return ft ? ft.toLowerCase() : "npc feature";
    }
    if (type === "license") return this.manufacturer(e.source)?.name ?? e.source ?? "";
    if (type === "core_bonus") return this.manufacturer(e.source)?.name ?? e.source ?? "core bonus";
    if (type === "tag") return "rules keyword";
    if (type === "manufacturer") return (e.id || "").toUpperCase();
    if (type === "pilot_gear") return e.gearType ?? "Gear";
    if (type === "background") return "pilot origin";
    return "";
  }
}

/** Bridge a runtime `CompendiumType` to the typed `Compendium.getById` (which wants an `ItemType`). */
function compendiumGet(compendium: Compendium, type: CompendiumType, id: string): unknown {
  return compendium.getById(type as ItemType, id);
}

/**
 * Resolve a manufacturer id to its real brand accent (`manufacturer.dark`) + a derived
 * archive-ground tint. A known maker yields its own brand color; an unknown id (no brand
 * record) or `undefined` yields the {@link NEUTRAL_ACCENT} neutral fallback. This is the
 * theming seam: detail headers, grouped-list bars, and search dots all resolve their color
 * through here.
 */
export function mfrStyle(id: string | undefined): { accent: string; tint: string } {
  const accent = id ? BRAND_BY_ID.get(id) : undefined;
  if (!accent) return { accent: NEUTRAL_ACCENT, tint: NEUTRAL_TINT };
  return { accent, tint: tintOf(accent) };
}

/** Tag ref → display label, substituting the `{VAL}` placeholder (or dropping it). */
export function tagLabel(source: CompendiumSource, ref: TagRef): { id: string; label: string } {
  const tg = source.raw("tag", ref.id) as Tag | undefined;
  const name = tg?.name ?? ref.id;
  const label = ref.val != null ? name.replace(/\{VAL\}/g, String(ref.val)) : name.replace(/\s*\{VAL\}/g, "");
  return { id: ref.id, label };
}

export const PACK_LINE = "CORE · LONG RIM";

/**
 * Display labels for `pilot_*` bonus ids (`packages/rules/src/compute.ts`'s
 * `computePilotStats` applier keys — the same ids the engine folds into the pilot's derived
 * combat stats). Anything not listed falls back to `pilot_` stripped + spaced + upper-cased, so a
 * future `pilot_*` bonus id doesn't silently vanish from a card/row.
 */
export const PILOT_BONUS_LABEL: Record<string, string> = {
  pilot_hp: "HP",
  pilot_armor: "ARMOR",
  pilot_evasion: "EVASION",
  pilot_edef: "E-DEF",
  pilot_speed: "SPEED",
};

/**
 * A pilot-gear item's stat profile — damage/range rows (weapons) plus `bonuses[]` rows
 * (armor), the single source both the COMPENDIUM card ({@link buildDetail}'s `pilot_gear`
 * branch in `compendiumView.ts`) and the PILOT sheet's held-gear rows in EDIT
 * (`PilotSheetEdit.tsx`) render. `replace`/`overwrite` bonuses render `=N` (the item
 * SETS the stat, displacing the pilot's base — see `packages/rules/src/bonus.ts`
 * `applyBonus`), distinct from a plain additive `+N`; no pilot gear uses `overwrite` today,
 * so it gets the same `=` treatment as `replace` deliberately, not by accident (see
 * `packages/rules/src/bonus.ts`'s overwrite > replace > additive precedence).
 */
export function pilotGearProfile(g: PilotGear): { k: string; v: string | number }[] {
  const profile: { k: string; v: string | number }[] = [];
  for (const x of (g.damage as Damage[] | undefined) ?? []) profile.push({ k: String(x.type).toUpperCase(), v: x.val });
  for (const x of (g.range as RangeVal[] | undefined) ?? []) profile.push({ k: String(x.type).toUpperCase(), v: x.val });
  for (const b of g.bonuses ?? []) {
    const label = PILOT_BONUS_LABEL[b.id] ?? b.id.replace(/^pilot_/, "").replace(/_/g, " ").toUpperCase();
    const sign = b.replace || b.overwrite ? "=" : "+";
    profile.push({ k: label, v: `${sign}${b.val}` });
  }
  return profile;
}

// re-exports for the component
export { TYPE_LABEL, TYPE_SING, ROMAN, DMG_COL, MFR_ORDER };
export type { Frame, Weapon, System, Mod, Talent, Skill, PilotGear, CoreBonus, Damage, RangeVal, NpcClass, NpcTemplate, NpcFeature };
